import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CloudSun, ShieldAlert, Thermometer, ChevronDown, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const TONE = {
  warning: "border-red-300 bg-red-50 text-red-900",
  watch: "border-amber-300 bg-amber-50 text-amber-900",
  advisory: "border-yellow-300 bg-yellow-50 text-yellow-900",
  monitor: "border-royal/20 bg-royal/5 text-royal-deep",
  normal: "border-border bg-secondary text-foreground",
};

const Row = ({ r }) => (
  <Link
    to={`/properties/${r.property_id}`}
    data-testid={`weather-row-${r.property_id}`}
    className={`block rounded-lg border px-3.5 py-3 transition-transform duration-200 hover:scale-[1.01] ${
      TONE[r.severity] || TONE.normal
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <p className="flex items-center gap-1.5 text-sm font-semibold min-w-0">
        {r.needs_attention && r.severity === "warning" && <ShieldAlert className="h-3.5 w-3.5 shrink-0" />}
        <span className="truncate">{r.property_name}</span>
      </p>
      {(r.temperature_f != null || r.period_temp_f != null) && (
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium opacity-90">
          <Thermometer className="h-3 w-3" />
          {r.temperature_f ?? r.period_temp_f}°F
        </span>
      )}
    </div>
    <p className="mt-1 text-xs opacity-90 line-clamp-2">
      {r.alerts?.length ? r.alerts.join(" · ") : r.forecast || r.headline || "Monitoring"}
    </p>
    <p className="mt-1.5 text-[11px] uppercase tracking-wider opacity-70">
      {r.status === "current" ? r.severity : `data ${r.status}`}
      {r.precip_probability_pct ? ` · ${r.precip_probability_pct}% rain` : ""}
      {r.wind ? ` · wind ${r.wind}` : ""}
      {r.precision === "city" ? " · city-level" : ""}
      {r.tropical_status && r.tropical_status !== "none"
        ? ` · tropical: ${r.tropical_status.replace(/_/g, " ")}`
        : ""}
    </p>
  </Link>
);

/** Dashboard weather: attention first, every property available underneath. Visible to all roles. */
export function PortfolioWeather() {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/weather/overview");
      setData(data);
    } catch {
      setData({ status: "error", properties: [] });
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, [load]);

  const refresh = async () => {
    setBusy(true);
    await load();
    setBusy(false);
  };

  if (!data || data.status === "disabled" || !data.properties?.length) return null;
  const attention = data.properties.filter((r) => r.needs_attention);
  const calm = data.properties.filter((r) => !r.needs_attention);
  const shown = showAll ? data.properties : attention.length ? attention : data.properties.slice(0, 3);

  return (
    <section data-testid="portfolio-weather" className="rounded-xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 text-base md:text-lg font-display font-semibold text-royal-deep">
          <CloudSun className="h-4 w-4 text-royal" /> Weather intelligence
        </h2>
        <span className="text-xs text-muted-foreground">
          {attention.length
            ? `${attention.length} need${attention.length === 1 ? "s" : ""} attention`
            : "All clear"}{" "}
          · {data.tracked || data.properties.length} tracked
        </span>
        <button
          data-testid="weather-refresh-btn"
          onClick={refresh}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-royal hover:border-royal/40 transition-colors duration-200"
        >
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        Forecasts and alerts from the National Weather Service, tropical outlooks from the National
        Hurricane Center.
      </p>

      <div className="mt-3.5 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((r) => (
          <Row key={r.property_id} r={r} />
        ))}
      </div>

      {calm.length > 0 && (
        <button
          data-testid="weather-show-all-btn"
          onClick={() => setShowAll((v) => !v)}
          className="mt-3.5 inline-flex items-center gap-1.5 text-xs font-semibold text-royal hover:text-royal-deep transition-colors duration-200"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${showAll ? "rotate-180" : ""}`} />
          {showAll ? "Show only what needs attention" : `Show all ${data.properties.length} properties`}
        </button>
      )}
    </section>
  );
}

export default PortfolioWeather;
